<template>
  <div class="article-sidebar__wrapper rounded-corners">
    <!-- Sync with ArticleBelowBody/index.vue -->
    <a-visibility hide :to="$breakpoint.desktopMd" :keep-on-client="false">
      <a-buzz-sprout-player class="article-sidebar__buzz-widget" />
    </a-visibility>

    <div class="article-sidebar__video-banner">
      <article-video-banner :layout="$options.consts.FIXED_LAYOUT.DESKTOP" />
    </div>
    <a-newsletter-form-v2 class="article-sidebar__newsletter-form" />
    <sidebar-most-viewed
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.MOST_VIEWED
        )
      "
      :articles="mostViewedArticles"
      class="article-sidebar__most-viewed"
      @mounted="
        $_lazyHydratedComponentState_onHydratedComponentMounted(
          $options.consts.HYDRATED_SECTION_NAME.MOST_VIEWED
        )
      "
    />
    <sidebar-latest-news
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.LATEST_NEWS
        )
      "
      :data-ref="$options.consts.REFS.ARTICLE_PAGE_SIDEBAR_LATEST_NEWS"
      :articles="latestNewsArticles"
      class="article-sidebar__latest-news"
      @mounted="
        $_lazyHydratedComponentState_onHydratedComponentMounted(
          $options.consts.HYDRATED_SECTION_NAME.LATEST_NEWS
        )
      "
    />
    <sidebar-education-center
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.EDUCATION_CENTER
        )
      "
      :articles="educationCenterArticles"
      class="article-sidebar__education-center"
      @mounted="
        $_lazyHydratedComponentState_onHydratedComponentMounted(
          $options.consts.HYDRATED_SECTION_NAME.EDUCATION_CENTER
        )
      "
    />
    <sidebar-keep-reading
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.KEEP_READING
        )
      "
      :articles="keepReadingArticles"
      class="article-sidebar__keep-reading"
      @mounted="
        $_lazyHydratedComponentState_onHydratedComponentMounted(
          $options.consts.HYDRATED_SECTION_NAME.KEEP_READING
        )
      "
    />
    <sidebar-trending
      :key="
        $_lazyHydratedComponentState_getHydratedComponentRefreshKey(
          $options.consts.HYDRATED_SECTION_NAME.TRENDING
        )
      "
      :articles="trendingArticles"
      class="article-sidebar__trending"
      @mounted="
        $_lazyHydratedComponentState_onHydratedComponentMounted(
          $options.consts.HYDRATED_SECTION_NAME.TRENDING
        )
      "
    />
  </div>
</template>

<script>
import { PROP_TYPES, propValidator } from '@/utils/validators'
import ArticleVideoBanner from '@/components/_sections/article/AArticle/ArticleVideoBanner'
import { FIXED_LAYOUT } from 'enums/fixed-layout'
import { REFS } from 'enums/external-refs'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import mixins from '@/utils/mixins'
import { RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME } from 'enums/article'
import ABuzzSproutPlayer from 'shared/ABuzzSproutPlayer/index.vue'
import AVisibility from 'shared/AVisibility/index.vue'

const HYDRATED_SECTION_NAME = {
  MOST_VIEWED: 'MOST_VIEWED',
  LATEST_NEWS: 'LATEST_NEWS',
  EDUCATION_CENTER: 'EDUCATION_CENTER',
  KEEP_READING: 'KEEP_READING',
  TRENDING: 'TRENDING'
}

export default {
  name: 'ArticleSidebar',
  mixins: [mixins.lazyHydratedComponentState],
  components: {
    AVisibility,
    ABuzzSproutPlayer,
    ArticleVideoBanner,
    ANewsletterFormV2: hydrateWhenVisible(() =>
      import('shared/ANewsletterFormV2')
    ),
    SidebarMostViewed: hydrateWhenVisible(() => import('./SidebarMostViewed'), {
      props: ['articles']
    }),
    SidebarLatestNews: hydrateWhenVisible(() => import('./SidebarLatestNews'), {
      props: ['articles']
    }),
    SidebarEducationCenter: hydrateWhenVisible(
      () => import('./SidebarEducationCenter'),
      {
        props: ['articles']
      }
    ),
    SidebarKeepReading: hydrateWhenVisible(
      () => import('./SidebarKeepReading'),
      {
        props: ['articles']
      }
    ),
    SidebarTrending: hydrateWhenVisible(() => import('./SidebarTrending'), {
      props: ['articles']
    })
  },
  props: {
    mostViewedArticles: propValidator([PROP_TYPES.ARRAY]),
    latestNewsArticles: propValidator([PROP_TYPES.ARRAY]),
    educationCenterArticles: propValidator([PROP_TYPES.ARRAY]),
    keepReadingArticles: propValidator([PROP_TYPES.ARRAY]),
    trendingArticles: propValidator([PROP_TYPES.ARRAY])
  },
  consts: { REFS, FIXED_LAYOUT, HYDRATED_SECTION_NAME },
  methods: {
    rerenderNonHydratedComponents() {
      this.$_lazyHydratedComponentState_rerenderNonHydratedComponents(
        Object.values(HYDRATED_SECTION_NAME)
      )
    }
  },
  beforeMount() {
    this.$bus.$on(
      RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME,
      this.rerenderNonHydratedComponents
    )
  },
  beforeDestroy() {
    this.$bus.$off(
      RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME,
      this.rerenderNonHydratedComponents
    )
  }
}
</script>

<style scoped lang="scss">
@import '~assets/scss/local/styles/article';

$sidebar-background: #efefef;

.article-sidebar__wrapper {
  padding: 15px 15px 30px;
  background: $sidebar-background;

  .article-sidebar__buzz-widget {
    margin-bottom: 15px;
  }

  .article-sidebar__video-banner {
    width: $article-sidebar-width - 2 * 15px; // Sidebar width - paddings
    height: 244px;
  }

  /deep/ .article-sidebar__newsletter-form {
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 0;

    @extend .sidebar-section-top-margin;

    .input__wrapper {
      background: $sidebar-background;
    }

    .google-captcha-policy {
      margin-bottom: 0;

      @extend .tiny-text;
    }

    .gdpr__checkbox .checkbox__label-text p {
      @extend .tiny-text;
    }
  }

  /deep/ .article-sidebar__most-viewed,
  /deep/ .article-sidebar__latest-news,
  /deep/ .article-sidebar__education-center,
  /deep/ .article-sidebar__keep-reading,
  /deep/ .article-sidebar__trending {
    @extend .sidebar-section-top-margin;
  }
}
</style>
